import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Building2, Eye, Edit, Trash2, Settings, BarChart3, Users, FileText, Shield, AlertCircle } from 'lucide-react';
import permissionService from 'features/administrativo/services/permissionService';
import PermissionFallback from './PermissionFallback';
import api from 'services/api';

const PermissionSelector = ({ 
  _userId,
  onPermissionsChange, 
  initialPermissions = [], 
  initialCompanies = [],
  disabled = false 
}) => {
  const [permissions, setPermissions] = useState([]);
  const [userPermissions, setUserPermissions] = useState(initialPermissions);
  const [selectedCompanies, setSelectedCompanies] = useState(initialCompanies);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para presets
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  
  // Estados para renderização incremental
  const [progressoCarregamento, setProgressoCarregamento] = useState({
    permissions: false,
    companies: false,
    presets: false
  });
  const [loadingIncremental, setLoadingIncremental] = useState(false);

  // Atualizar estado quando as props iniciais mudarem
  useEffect(() => {
    setUserPermissions(initialPermissions);
    setSelectedCompanies(initialCompanies);
  }, [initialPermissions, initialCompanies]);

  // Ícones para categorias de permissões
  const categoryIcons = {
    dashboard: BarChart3,
    users: Users,
    permissions: Settings,
    access_requests: AlertCircle,
    comercial: Building2,
    financeiro: BarChart3,
    contratos: FileText,
    operacional: Settings,
    noc: Shield,
    marketing: Building2,
    suporte: Users,
    system: Settings
  };

  // Cores para categorias
  const categoryColors = {
    dashboard: 'bg-blue-50 border-blue-200 text-blue-800',
    users: 'bg-green-50 border-green-200 text-green-800',
    permissions: 'bg-orange-50 border-orange-200 text-orange-800',
    access_requests: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    comercial: 'bg-indigo-50 border-indigo-200 text-indigo-800',
    financeiro: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    contratos: 'bg-cyan-50 border-cyan-200 text-cyan-800',
    operacional: 'bg-gray-50 border-gray-200 text-gray-800',
    noc: 'bg-red-50 border-red-200 text-red-800',
    marketing: 'bg-pink-50 border-pink-200 text-pink-800',
    suporte: 'bg-teal-50 border-teal-200 text-teal-800',
    system: 'bg-slate-50 border-slate-200 text-slate-800'
  };

  const loadPermissions = useCallback(async (isIncremental = false) => {
    try {
      const response = await permissionService.getAllPermissions();
      
      if (response.success) {
        setPermissions(response.data);
        if (isIncremental) {
          setProgressoCarregamento(prev => ({ ...prev, permissions: true }));
        }
      } else {
        console.error('❌ Erro na resposta da API:', response);
        setError('Erro na resposta da API: ' + (response.message || 'Resposta inválida'));
      }
    } catch (error) {
      console.error('❌ Erro ao carregar permissões:', error);
      setError('Erro ao carregar permissões: ' + error.message);
    }
  }, []);

  const loadManageableCompanies = useCallback(async (isIncremental = false) => {
    try {
      const response = await permissionService.getManageableCompanies();
      
      if (response.success) {
        setAvailableCompanies(response.data.companies || []);
        if (isIncremental) {
          setProgressoCarregamento(prev => ({ ...prev, companies: true }));
        }
      } else {
        console.error('❌ Erro ao carregar empresas:', response);
        setError('Erro ao carregar empresas: ' + (response.message || 'Resposta inválida'));
      }
    } catch (error) {
      console.error('❌ Erro ao carregar empresas:', error);
      setError('Erro ao carregar empresas: ' + error.message);
    }
  }, []);

  const loadPresets = useCallback(async (isIncremental = false) => {
    try {
      const response = await api.get('/permission-presets');
      
      if (response.data && response.data.success) {
        setPresets(response.data.data || []);
      } else {
        setPresets(response.data || []);
      }
      
      if (isIncremental) {
        setProgressoCarregamento(prev => ({ ...prev, presets: true }));
      }
    } catch (error) {
      console.error('Erro ao carregar presets:', error);
      setPresets([]);
    }
  }, []);

  const loadData = useCallback(async () => {
    setLoadingIncremental(true);
    setError(null);
    
    // Resetar progresso
    setProgressoCarregamento({
      permissions: false,
      companies: false,
      presets: false
    });
    
    try {
      
      // Carregar permissões primeiro (mais importante)
      await loadPermissions(true);
      
      // Carregar empresas e presets em paralelo
      loadManageableCompanies(true);
      loadPresets(true);
      
    } catch (error) {
      console.error('❌ Erro ao carregar dados:', error);
      setError('Erro ao carregar dados: ' + error.message);
    } finally {
      // Finalizar loading após um delay para permitir renderização incremental
      setTimeout(() => {
        setLoading(false);
        setLoadingIncremental(false);
      }, 200);
    }
  }, [loadPermissions, loadManageableCompanies, loadPresets]);

  // Monitorar progresso da renderização incremental
  useEffect(() => {
    if (loadingIncremental) {
      const todasCompletas = Object.values(progressoCarregamento).every(completo => completo);
      if (todasCompletas) {
        setLoadingIncremental(false);
        setLoading(false);
      }
    }
  }, [progressoCarregamento, loadingIncremental]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePermissionToggle = useCallback((permissionName) => {
    if (disabled) return;
    
    const newPermissions = userPermissions.includes(permissionName)
      ? userPermissions.filter(p => p !== permissionName)
      : [...userPermissions, permissionName];
    
    setUserPermissions(newPermissions);
    onPermissionsChange?.(newPermissions, selectedCompanies);
  }, [disabled, userPermissions, selectedCompanies, onPermissionsChange]);

  const handleCompanyToggle = useCallback((companyId) => {
    if (disabled) return;
    
    const newCompanies = selectedCompanies.includes(companyId)
      ? selectedCompanies.filter(id => id !== companyId)
      : [...selectedCompanies, companyId];
    
    setSelectedCompanies(newCompanies);
    onPermissionsChange?.(userPermissions, newCompanies);
  }, [disabled, selectedCompanies, userPermissions, onPermissionsChange]);

  const handlePresetChange = useCallback((presetName) => {
    setSelectedPreset(presetName);
  }, []);

  const handleApplyPreset = useCallback(async () => {
    if (!selectedPreset) return;
    
    try {
      // Buscar o preset selecionado
      const preset = presets.find(p => p.name === selectedPreset);
      if (preset) {
        // Aplicar as permissões do preset
        setUserPermissions(preset.permissions);
        onPermissionsChange?.(preset.permissions, selectedCompanies);
        setSelectedPreset('');
      }
    } catch (error) {
      console.error('Erro ao aplicar preset:', error);
    }
  }, [selectedPreset, presets, selectedCompanies, onPermissionsChange]);

  const getPermissionIcon = useCallback((permission) => {
    if (permission.includes('view')) return Eye;
    if (permission.includes('edit') || permission.includes('create')) return Edit;
    if (permission.includes('delete')) return Trash2;
    if (permission.includes('manage')) return Settings;
    return Settings;
  }, []);

  const getPermissionColor = useCallback((permission) => {
    if (permission.includes('view')) return 'text-blue-600';
    if (permission.includes('edit') || permission.includes('create')) return 'text-green-600';
    if (permission.includes('delete')) return 'text-red-600';
    if (permission.includes('manage')) return 'text-purple-600';
    return 'text-gray-600';
  }, []);

  // Agrupar permissões por categoria
  const permissionsByCategory = useMemo(() => {
    if (!Array.isArray(permissions) || permissions.length === 0) {
      return {};
    }
    return (permissions || []).reduce((acc, permission) => {
      const category = permission.category || 'system';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(permission);
      return acc;
    }, {});
  }, [permissions]);

  if (loading) {
    // Mostrar progresso incremental se disponível
    if (loadingIncremental && progressoCarregamento) {
      const progresso = Object.values(progressoCarregamento).filter(Boolean).length;
      const total = Object.keys(progressoCarregamento).length;
      const percentual = Math.round((progresso / total) * 100);
      
      return (
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-lg font-medium text-gray-900 mb-2">
              Carregando permissões...
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${percentual}%` }}
              ></div>
            </div>
            <div className="text-sm text-gray-600">
              {percentual}% - {progresso} de {total} seções carregadas
            </div>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className={`flex items-center ${progressoCarregamento.permissions ? 'text-green-600' : 'text-gray-500'}`}>
              {progressoCarregamento.permissions ? '✓' : '⏳'} Permissões
            </div>
            <div className={`flex items-center ${progressoCarregamento.companies ? 'text-green-600' : 'text-gray-500'}`}>
              {progressoCarregamento.companies ? '✓' : '⏳'} Empresas
            </div>
            <div className={`flex items-center ${progressoCarregamento.presets ? 'text-green-600' : 'text-gray-500'}`}>
              {progressoCarregamento.presets ? '✓' : '⏳'} Presets
            </div>
          </div>
        </div>
      );
    }
    
    return <PermissionFallback loading={true} />;
  }

  if (error) {
    return <PermissionFallback error={error} onRetry={loadPermissions} />;
  }

  if (!Array.isArray(permissions) || permissions.length === 0) {
    return <PermissionFallback />;
  }

  return (
    <div className="space-y-6">
      {/* Seleção de Empresas */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
          <Building2 className="h-5 w-5 mr-2" />
          Acesso por Empresa
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {availableCompanies.map((company) => (
            <div key={company.id} className="p-3 border rounded-lg">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedCompanies.includes(company.id)}
                  onChange={() => handleCompanyToggle(company.id)}
                  disabled={disabled}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex items-center space-x-2">
                  {company.color && (
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: company.color }}
                    ></div>
                  )}
                  <span className="text-sm font-medium text-gray-700">
                    {company.display_name}
                  </span>
                </div>
              </label>
            </div>
          ))}
        </div>
        
        {availableCompanies.length === 0 && (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              Nenhuma empresa disponível para gerenciamento. Verifique suas permissões.
            </p>
          </div>
        )}
      </div>

      {/* Seleção de Presets */}
      {presets.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
            <Shield className="h-5 w-5 mr-2" />
            Aplicar Preset de Permissões
          </h3>
          <div className="p-4 bg-blue-50 rounded-lg">
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <select
                value={selectedPreset}
                onChange={(e) => handlePresetChange(e.target.value)}
                className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-0"
                disabled={disabled}
              >
                <option value="">Selecione um preset...</option>
                {presets.map(preset => (
                  <option key={preset.name} value={preset.name}>
                    {preset.label} - {preset.description}
                  </option>
                ))}
              </select>
              <button
                onClick={handleApplyPreset}
                disabled={!selectedPreset || disabled}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Seleção de Permissões */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
          <Shield className="h-5 w-5 mr-2" />
          Permissões Específicas
        </h3>
        <div className="space-y-4">
          {Object.entries(permissionsByCategory).map(([category, categoryPermissions]) => {
            const IconComponent = categoryIcons[category] || Settings;
            const categoryColor = categoryColors[category] || 'bg-gray-50 border-gray-200 text-gray-800';
            
            return (
              <div key={category} className="border rounded-lg">
                <div className={`px-4 py-3 border-b ${categoryColor}`}>
                  <div className="flex items-center">
                    <IconComponent className="h-4 w-4 mr-2" />
                    <span className="font-medium capitalize">{category}</span>
                    <span className="ml-2 text-xs opacity-75">
                      ({categoryPermissions.length} permissões)
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {categoryPermissions.map((permission) => {
                      const PermissionIcon = getPermissionIcon(permission.name);
                      const iconColor = getPermissionColor(permission.name);
                      const isSelected = userPermissions.includes(permission.name);
                      
                      return (
                        <label
                          key={permission.name}
                          className={`flex items-start space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            isSelected 
                              ? 'bg-blue-50 border-blue-200' 
                              : 'bg-white border-gray-200 hover:bg-gray-50'
                          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handlePermissionToggle(permission.name)}
                            disabled={disabled}
                            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <PermissionIcon className={`h-4 w-4 ${iconColor}`} />
                              <span className="text-sm font-medium text-gray-900">
                                {permission.display_name || permission.name}
                              </span>
                            </div>
                            {permission.description && (
                              <p className="text-xs text-gray-500 mt-1">
                                {permission.description}
                              </p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Resumo das Seleções */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Resumo das Permissões</h4>
        <div className="text-sm text-gray-600">
          <p><strong>Empresas:</strong> {selectedCompanies.length} selecionada(s)</p>
          <p><strong>Permissões:</strong> {userPermissions.length} selecionada(s)</p>
          {selectedCompanies.includes('group') && (
            <p className="text-blue-600 font-medium">⚠️ Acesso ao grupo selecionado - usuário terá acesso a todas as empresas</p>
          )}
          {selectedCompanies.length > 0 && (
            <div className="mt-2">
              <p className="font-medium text-gray-700">Empresas selecionadas:</p>
              <ul className="list-disc list-inside mt-1">
                {selectedCompanies.map(companyId => {
                  const company = availableCompanies.find(c => c.id === companyId);
                  return (
                    <li key={companyId} className="text-xs">
                      {company ? company.display_name : `Empresa ${companyId}`}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionSelector;
